import { FastDetectionObject, FastDetectionResponseDTO } from '../../../api/ai.api.types';
import { RemoteResult } from '../../common/RemoteResultCommand/index.types';
import { AI_FAST_DETECT_MIN_CONFIDENCE } from './liveConstants';

// Picks the highest-confidence object of `category` that meets the minimum confidence, or
// undefined when none qualifies.
const bestObjectByCategory = (
    objects: Array<FastDetectionObject>,
    category: FastDetectionObject['category'],
): FastDetectionObject | undefined => {
    return objects
        .filter((o) => o.category === category && o.confidence >= AI_FAST_DETECT_MIN_CONFIDENCE)
        .reduce<FastDetectionObject | undefined>((best, current) => (!best || current.confidence > best.confidence ? current : best), undefined);
};

// Maps a fast-detection (PicoDet) response to a boxes-only RemoteResult. This endpoint is
// detection-only — it never carries a meter number or readable values — so the live flow's
// existing "nothing to match/accumulate" handling for missing number/rates is the contract here;
// only detectedMeterBox/detectedDisplayBox are ever populated.
// `code` objects are ignored — barcode/DataMatrix decoding happens server-side in evaluateImage.
// Returns undefined when neither a meter nor a display object was found — this mirrors "server
// saw nothing" and triggers the existing box-drop behavior downstream.
export const mapFastDetectionToRemoteResult = (response: FastDetectionResponseDTO | undefined): RemoteResult | undefined => {
    if (!response) {
        return undefined;
    }

    const bestMeter = bestObjectByCategory(response.objects, 'meter');
    const bestDisplay = bestObjectByCategory(response.objects, 'display');

    if (!bestMeter && !bestDisplay) {
        return undefined;
    }

    return {
        meterNumber: undefined,
        meterNumberConfidence: 0,
        meterRates: [],
        detectedMeterBox: bestMeter?.box,
        detectedDisplayBox: bestDisplay?.box,
        meta: {
            expectedMeterNumber: undefined,
            detectedMeterNumber: undefined,
            expectedObis: undefined,
            detectedObis: undefined,
            expectedReadingValue: undefined,
            detectedReadingValue: undefined,
            expectedMedium: undefined,
            detectedMedium: undefined,
            warnings: undefined,
        },
    };
};
